// Player movement state machine (pure, no Phaser). Each frame the Phaser side
// feeds it the input snapshot and the body's post-physics contact flags and
// velocity; it returns the velocity / gravity to apply for the next physics
// steps plus one-frame event flags (jumped, landed, dash start/end, drop).
//
// Implements every feel rule from DESIGN §4: ground/air accel & decel, coyote
// time, jump buffering, variable jump height, apex hang, fall gravity, max
// fall, air jumps, glide, dash (i-frames, recovery, cooldown, air charges),
// drop-through and ability motion overrides.

import type { MotionOverride } from '../../core/contracts';
import { FEEL, approach, clamp, gravityMultiplier, stepHorizontal, type MoveTuning } from './movement';

export interface ControlInput {
  /** -1..1 horizontal axis. */
  moveX: number;
  jumpHeld: boolean;
  /** Seconds since jump was pressed (Infinity if never or consumed) — buffering. */
  jumpAge: number;
  downHeld: boolean;
  /** Seconds since dash was pressed (Infinity if never or consumed). */
  dashAge: number;
}

export const NO_INPUT: Readonly<ControlInput> = Object.freeze({
  moveX: 0,
  jumpHeld: false,
  jumpAge: Infinity,
  downHeld: false,
  dashAge: Infinity,
});

export interface ControlSensors {
  /** Physics contact below the body (blocked/touching down). */
  onGround: boolean;
  /** Standing on a one-way platform (down + jump drops through). */
  onOneWay: boolean;
  blockedLeft: boolean;
  blockedRight: boolean;
  blockedUp: boolean;
  /** Current body velocity after collisions. */
  vx: number;
  vy: number;
}

export type DashPhase = 'none' | 'dash' | 'recovery';

export interface ControlOutput {
  vx: number;
  vy: number;
  /** Gravity for the body this frame in px/s² (0 = gravity off). */
  gravity: number;
  /** Cap on downward speed. */
  maxFall: number;
  consumeJump: boolean;
  consumeDash: boolean;
  /** 0 = no jump, 1 = ground jump (incl. coyote), 2 = air jump. */
  jumped: 0 | 1 | 2;
  landed: boolean;
  /** Downward speed at touchdown (px/s). */
  landImpact: number;
  dashStarted: boolean;
  dashEnded: boolean;
  dropped: boolean;
  overrideEnded: boolean;
}

interface ActiveOverride {
  m: MotionOverride;
  time: number;
  lastVy: number;
}

/** Minimum rise time before a released jump is cut (keeps tapped hops readable). */
export const MIN_JUMP_TIME = 0.05;

export class MovementController {
  tuning: MoveTuning;

  onGround = true;
  sinceGround = 0;
  jumpedSinceGround = false;
  airJumpsLeft: number;
  airDashesLeft: number;
  /** A player jump is in progress (variable height + apex hang apply). */
  jumpActive = false;
  sinceTakeoff = Infinity;
  gliding = false;

  dashPhase: DashPhase = 'none';
  /** Time spent in the current dash phase. */
  dashTime = 0;
  dashDir: 1 | -1 = 1;
  dashCooldown = 0;
  /** Remaining dash invulnerability. */
  iframes = 0;
  /** Added to the dash i-frames when a dash starts (PHASE_CAPACITOR). */
  dashIFrameBonus = 0;
  knockbackTime = 0;
  /** > 0 while dropping through one-way platforms. */
  dropTime = 0;

  private dashGrounded = false;
  private lastAirVy = 0;
  private ov: ActiveOverride | null = null;

  readonly out: ControlOutput = {
    vx: 0,
    vy: 0,
    gravity: 0,
    maxFall: 0,
    consumeJump: false,
    consumeDash: false,
    jumped: 0,
    landed: false,
    landImpact: 0,
    dashStarted: false,
    dashEnded: false,
    dropped: false,
    overrideEnded: false,
  };

  constructor(tuning: MoveTuning) {
    this.tuning = tuning;
    this.airJumpsLeft = tuning.airJumps;
    this.airDashesLeft = tuning.airDashes;
  }

  /** New stats → new tuning. Charges above a lowered maximum are clamped; raised maxima refill on landing. */
  setTuning(t: MoveTuning): void {
    this.tuning = t;
    this.airJumpsLeft = Math.min(this.airJumpsLeft, t.airJumps);
    this.airDashesLeft = Math.min(this.airDashesLeft, t.airDashes);
    if (this.onGround) {
      this.airJumpsLeft = t.airJumps;
      this.airDashesLeft = t.airDashes;
    }
  }

  get dashing(): boolean {
    return this.dashPhase === 'dash';
  }

  get recovering(): boolean {
    return this.dashPhase === 'recovery';
  }

  /** 0..1 across dash + recovery (drives the dash sheet). */
  get dashProgress(): number {
    const t = this.tuning;
    const total = Math.max(1e-4, t.dashDuration + t.dashRecovery);
    if (this.dashPhase === 'dash') return Math.min(1, this.dashTime / total);
    if (this.dashPhase === 'recovery') return Math.min(1, (t.dashDuration + this.dashTime) / total);
    return 1;
  }

  get override(): MotionOverride | null {
    return this.ov ? this.ov.m : null;
  }

  get overrideProgress(): number {
    const ov = this.ov;
    if (!ov) return 0;
    return ov.m.duration > 0 ? Math.min(1, ov.time / ov.m.duration) : 1;
  }

  /** Begin an ability motion override. Returns true if a dash was interrupted (caller emits dashEnd). */
  startOverride(m: MotionOverride): boolean {
    if (this.ov) this.finishOverride();
    const interrupted = this.dashPhase === 'dash';
    this.dashPhase = 'none';
    this.dashTime = 0;
    this.jumpActive = false;
    this.gliding = false;
    this.knockbackTime = 0;
    this.ov = { m, time: 0, lastVy: Number.NaN };
    return interrupted;
  }

  /** End the override now (its onEnd runs). */
  cancelOverride(): void {
    if (this.ov) this.finishOverride();
  }

  /** Knockback reduces control briefly. Ignored during dashes and overrides. Returns true if accepted. */
  knockback(): boolean {
    if (this.dashPhase === 'dash' || this.ov) return false;
    this.knockbackTime = FEEL.knockbackTime;
    this.jumpActive = false;
    this.gliding = false;
    return true;
  }

  reduceCooldown(seconds: number): void {
    this.dashCooldown = Math.max(0, this.dashCooldown - Math.max(0, seconds));
  }

  /** Death / hard reset: no dash, no override (its onEnd still runs). */
  halt(): void {
    this.cancelOverride();
    this.dashPhase = 'none';
    this.dashTime = 0;
    this.iframes = 0;
    this.jumpActive = false;
    this.gliding = false;
  }

  step(inp: Readonly<ControlInput>, s: Readonly<ControlSensors>, facing: 1 | -1, dt: number): ControlOutput {
    const t = this.tuning;
    const o = this.out;
    o.consumeJump = false;
    o.consumeDash = false;
    o.jumped = 0;
    o.landed = false;
    o.landImpact = 0;
    o.dashStarted = false;
    o.dashEnded = false;
    o.dropped = false;
    o.overrideEnded = false;

    let vx = s.vx;
    let vy = s.vy;

    this.dashCooldown = Math.max(0, this.dashCooldown - dt);
    this.iframes = Math.max(0, this.iframes - dt);
    this.knockbackTime = Math.max(0, this.knockbackTime - dt);
    this.dropTime = Math.max(0, this.dropTime - dt);
    this.sinceTakeoff += dt;

    // ── Ground contact. A body moving up is never standing (the jump frame can still report the floor). ──
    const grounded = s.onGround && vy >= -1;
    const wasGround = this.onGround;
    this.onGround = grounded;
    if (grounded) {
      if (!wasGround) {
        o.landed = true;
        o.landImpact = Math.max(0, this.lastAirVy);
        this.jumpActive = false;
        this.gliding = false;
      }
      this.sinceGround = 0;
      this.jumpedSinceGround = false;
      this.airJumpsLeft = t.airJumps;
      this.airDashesLeft = t.airDashes;
      this.lastAirVy = 0;
    } else {
      this.sinceGround += dt;
      this.lastAirVy = vy;
    }

    // ── Ability override: velocity comes from the ability. ──
    const ov = this.ov;
    if (ov) {
      ov.time += dt;
      const m = ov.m;
      vx = m.vx;
      if (m.vy !== ov.lastVy) {
        vy = m.vy;
        ov.lastVy = m.vy;
      } else if (!m.gravity) {
        vy = m.vy;
      }
      o.gravity = m.gravity ? t.gravity * (vy > 0 ? t.fallGravityMult : 1) : 0;
      o.maxFall = t.maxFall;
      if (m.gravity && vy > t.maxFall) vy = t.maxFall;
      m.onUpdate?.(this.overrideProgress);
      if (this.ov === ov && ov.time >= m.duration) {
        this.finishOverride();
        o.overrideEnded = true;
        vx = clamp(vx, -t.runSpeed * FEEL.overrideExitSpeed, t.runSpeed * FEEL.overrideExitSpeed);
        vy = Math.max(vy, -t.airJumpVelocity * FEEL.overrideExitRise);
        o.gravity = t.gravity;
      }
      o.vx = vx;
      o.vy = vy;
      return o;
    }

    // ── Dash start: buffered press, cooldown ready and a charge available (ground dashes are free). ──
    if (
      this.dashPhase !== 'dash' &&
      inp.dashAge <= FEEL.dashBuffer &&
      this.dashCooldown <= 0 &&
      (grounded || this.airDashesLeft > 0)
    ) {
      this.dashDir = Math.abs(inp.moveX) > 0.25 ? (inp.moveX > 0 ? 1 : -1) : facing;
      this.dashPhase = 'dash';
      this.dashTime = 0;
      this.dashCooldown = t.dashCooldown;
      if (!grounded) this.airDashesLeft--;
      this.dashGrounded = grounded;
      this.iframes = t.dashIFrames + this.dashIFrameBonus;
      this.jumpActive = false;
      this.gliding = false;
      this.knockbackTime = 0;
      o.dashStarted = true;
      o.consumeDash = true;
    }

    // ── Dash: fixed horizontal speed, no gravity. Jumps pressed now stay buffered for the recovery. ──
    if (this.dashPhase === 'dash') {
      this.dashTime += dt;
      if (!s.onGround) this.dashGrounded = false; // ran off a ledge: continue as a level air dash
      const blocked = this.dashDir > 0 ? s.blockedRight : s.blockedLeft;
      vx = this.dashDir * t.dashSpeed;
      vy = this.dashGrounded ? FEEL.dashGroundStick : 0;
      o.gravity = 0;
      o.maxFall = t.maxFall;
      const wallStop = blocked && this.dashTime > FEEL.dashWallStop;
      if (this.dashTime >= t.dashDuration || wallStop) {
        this.dashPhase = 'recovery';
        this.dashTime = 0;
        o.dashEnded = true;
        vx = wallStop ? 0 : this.dashDir * Math.min(t.dashSpeed, t.runSpeed * FEEL.dashExitSpeed);
        vy = this.dashGrounded ? FEEL.dashGroundStick : 0;
        o.gravity = t.gravity;
      }
      o.vx = vx;
      o.vy = vy;
      return o;
    }

    // ── Recovery: reduced control, but jumping and firing are allowed. ──
    let control = 1;
    if (this.dashPhase === 'recovery') {
      this.dashTime += dt;
      if (this.dashTime >= t.dashRecovery) {
        this.dashPhase = 'none';
        this.dashTime = 0;
      } else {
        control = FEEL.recoveryControl;
      }
    }
    if (this.knockbackTime > 0) control = Math.min(control, FEEL.knockbackControl);

    // ── Jumps: buffered presses fire at the first legal moment. ──
    if (inp.jumpAge <= t.jumpBuffer) {
      const coyote = !grounded && this.sinceGround <= t.coyoteTime && !this.jumpedSinceGround;
      if (grounded && s.onOneWay && inp.downHeld) {
        this.dropTime = FEEL.dropTime;
        vy = FEEL.dropKick;
        this.onGround = false;
        this.jumpedSinceGround = true;
        o.dropped = true;
        o.consumeJump = true;
      } else if (grounded || coyote) {
        vy = -t.jumpVelocity;
        this.jumpActive = true;
        this.jumpedSinceGround = true;
        this.sinceTakeoff = 0;
        this.onGround = false;
        o.jumped = 1;
        o.consumeJump = true;
      } else if (this.airJumpsLeft > 0) {
        vy = -t.airJumpVelocity;
        this.airJumpsLeft--;
        this.jumpActive = true;
        this.sinceTakeoff = 0;
        this.gliding = false;
        o.jumped = 2;
        o.consumeJump = true;
      }
    }

    // ── Variable jump height / head bumps. ──
    if (this.jumpActive && o.jumped === 0) {
      if (s.blockedUp && vy <= 0) {
        this.jumpActive = false;
      } else if (!inp.jumpHeld) {
        if (vy < 0 && this.sinceTakeoff >= MIN_JUMP_TIME) {
          vy *= t.jumpCutMult;
          this.jumpActive = false;
        } else if (vy >= 0) {
          this.jumpActive = false;
        }
      }
    }

    // ── Gravity, glide and terminal velocity. ──
    o.gravity = t.gravity * gravityMultiplier(vy, inp.jumpHeld, this.jumpActive, t);
    let maxFall = t.maxFall;
    this.gliding = t.glideFallSpeed !== null && !this.onGround && inp.jumpHeld && vy > 0 && o.jumped === 0;
    if (this.gliding) {
      const g = t.glideFallSpeed as number;
      if (vy > g) vy = approach(vy, g, FEEL.glideBrake * dt);
      maxFall = Math.max(g, vy);
    }
    if (vy > maxFall) vy = maxFall;
    o.maxFall = maxFall;

    // ── Horizontal. ──
    vx = stepHorizontal(vx, inp.moveX, t, this.onGround, control, dt);

    o.vx = vx;
    o.vy = vy;
    return o;
  }

  private finishOverride(): void {
    const ov = this.ov;
    if (!ov) return;
    this.ov = null;
    ov.m.onEnd?.();
  }
}
